<template>
  <!-- The QR viewfinder, extracted from staffStreamAuto.vue (which renders it inline over its scanner area).
       Full screen (`fullscreen`): the view of composables/useQrScan.ts while it reads. The page (#app) is hidden so
       the camera preview behind the webview shows through; the frame and Cancelar sit outside #app. -->
  <div v-if="!fullscreen" class="w-64 h-64 bg-black/20 border-2 border-dashed border-white/80 rounded-lg flex justify-center items-center text-white/80 text-center p-4">
    <span>{{ hint }}</span>
  </div>
  <Teleport v-else to="body">
    <div v-if="active" class="fixed inset-0 flex flex-col items-center justify-between pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div class="mt-8 mx-6 rounded-xl bg-black/60 px-4 py-2 text-center text-white">
        {{ hint }}
      </div>
      <div class="flex flex-col items-center gap-8">
        <div class="w-64 h-64 bg-black/10 border-2 border-dashed border-white/80 rounded-lg" />
        <!-- The phone's flashlight as an iOS-style round toggle, apart from Cancelar: see-through off, white on.
             Through the scanner plugin (the camera is already running); off when it closes. -->
        <button
          v-if="hasLight"
          type="button"
          class="grid h-14 w-14 place-items-center rounded-full transition-colors duration-150"
          :class="lightOn ? 'bg-white text-gray-900' : 'bg-white/20 text-white'"
          role="switch"
          :aria-checked="lightOn"
          aria-label="Linterna"
          @click="toggleLight"
        >
          <vs-icon name="lucide:flashlight" :size="26" />
        </button>
      </div>
      <vs-button class="mb-10" kind="surface" iconName="lucide:x" text="Cancelar" @click="$emit('cancel')" />
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, onUnmounted, ref, watch } from 'vue'
import { vsButton, vsIcon } from '@isagaco/vuesaga'

export default defineComponent({
  name: 'QrScanOverlay',
  components: { vsButton, vsIcon },
  props: {
    fullscreen: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    hint: { type: String, default: 'Apunta la cámara al código' },
  },
  emits: ['cancel'],
  setup(props) {
    const lightOn = ref(false)
    const hasLight = ref(!!(window as any).QRScanner?.enableLight)
    const setLight = (on: boolean) => {
      const scanner = (window as any).QRScanner
      if (!scanner) return
      if (on) scanner.enableLight((err: any, status: any) => { lightOn.value = !err && !!status?.lightEnabled })
      else scanner.disableLight(() => { lightOn.value = false })
    }
    const toggleLight = () => setLight(!lightOn.value)
    watch(() => props.active, (active) => { if (!active && lightOn.value) setLight(false) })
    onUnmounted(() => { if (lightOn.value) setLight(false) })
    return { lightOn, hasLight, toggleLight }
  },
})
</script>

<style>
  html.qr-scan-active,
  html.qr-scan-active body {
    background: transparent !important;
  }
  html.qr-scan-active #app {
    visibility: hidden;
  }
</style>
